"use client";

import { memo } from "react";
import {
  type BasicRequestsSummaryFinding,
  buildCitationNumberMap,
  type SummarySection,
  sanitizeCitationIndices,
} from "@/types/aicheck";
import { blockMarkdownComponents, MarkdownText } from "./MarkdownText";

interface SummaryWithCitationsProps {
  summary: string;
  summarySections?: SummarySection[];
  findings: BasicRequestsSummaryFinding[];
  onCitationHover: (findingIndex: number, anchorRect: DOMRect) => void;
  onCitationLeave: () => void;
  onCitationClick: (findingIndex: number) => void;
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function buildMatchCandidates(text: string): string[] {
  const trimmed = text.trim();
  const firstLine = trimmed.split("\n")[0]?.trim() ?? "";
  const colonIndex = trimmed.indexOf(":");
  const headingPrefix =
    colonIndex >= 0 ? trimmed.slice(0, colonIndex + 1).trim() : "";

  return uniqueStrings([trimmed, firstLine, headingPrefix]);
}

function buildCitationLinks(
  findingIndices: number[],
  citationMap: Map<number, number>,
): string {
  return findingIndices
    .map((findingIndex) => {
      const citationNumber = citationMap.get(findingIndex) ?? "?";
      return `[${citationNumber}](#summary-citation-${findingIndex}-${citationNumber})`;
    })
    .join("");
}

function findInsertionIndex(
  summary: string,
  fromIndex: number,
  sectionText: string,
): number | null {
  for (const candidate of buildMatchCandidates(sectionText)) {
    const matchIndex = summary.indexOf(candidate, fromIndex);
    if (matchIndex >= 0) {
      return matchIndex + candidate.length;
    }
  }

  return null;
}

function annotateSummaryMarkdown(
  summary: string,
  sections: SummarySection[],
  findingsLength: number,
  citationMap: Map<number, number>,
): string {
  let output = "";
  let cursor = 0;

  for (const section of sections) {
    if (!section.text) continue;

    const insertionIndex = findInsertionIndex(summary, cursor, section.text);
    if (insertionIndex == null) {
      continue;
    }

    output += summary.slice(cursor, insertionIndex);

    const findingIndices = sanitizeCitationIndices(
      section.citation_indices,
      findingsLength,
    );
    if (findingIndices.length > 0) {
      output += buildCitationLinks(findingIndices, citationMap);
    }

    cursor = insertionIndex;
  }

  output += summary.slice(cursor);
  return output;
}

export const SummaryWithCitations = memo(function SummaryWithCitations({
  summary,
  summarySections,
  findings,
  onCitationHover,
  onCitationLeave,
  onCitationClick,
}: SummaryWithCitationsProps) {
  const sections = Array.isArray(summarySections) ? summarySections : [];
  const hasCitedSections =
    sections.length > 0 &&
    sections.some(
      (section) =>
        sanitizeCitationIndices(section.citation_indices, findings.length)
          .length > 0,
    );

  if (!hasCitedSections) {
    return (
      <div className="text-sm text-gray-700">
        <MarkdownText mode="block">{summary || "—"}</MarkdownText>
      </div>
    );
  }

  const citationMap = buildCitationNumberMap(sections, findings.length);
  const annotatedSummary = annotateSummaryMarkdown(
    summary,
    sections,
    findings.length,
    citationMap,
  );

  return (
    <div className="text-sm text-gray-700">
      <MarkdownText
        mode="block"
        components={{
          ...blockMarkdownComponents,
          a: ({ href, children }) => {
            const match = href?.match(/^#summary-citation-(\d+)-(\d+)$/);
            if (!match) {
              return (
                <a
                  href={href}
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  {children}
                </a>
              );
            }

            const findingIndex = Number(match[1]);
            const citationNumber = match[2];

            return (
              <button
                type="button"
                onMouseEnter={(event) => {
                  const rect = (
                    event.currentTarget as HTMLElement
                  ).getBoundingClientRect();
                  onCitationHover(findingIndex, rect);
                }}
                onMouseLeave={onCitationLeave}
                onClick={(event) => {
                  event.stopPropagation();
                  onCitationClick(findingIndex);
                }}
                className="inline-flex items-center align-super text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
                aria-label={`Open citation ${citationNumber}`}
              >
                [{citationNumber}]
              </button>
            );
          },
        }}
      >
        {annotatedSummary}
      </MarkdownText>
    </div>
  );
});
